/**
 * Maps one API question object to the shared Question view model.
 *
 * Belongs here: field normalization only (defaults, optional nesting).
 * Does not belong here: visibility rules or display formatting.
 */
import type {
  Question,
  QuestionInput,
  QuestionOption,
  QuestionnaireQuestionApi
} from '../shared/types/questionnaire.types'

export function mapApiQuestionToQuestion(api: QuestionnaireQuestionApi): Question {
  // Default missing input metadata — API may omit fields on partial payloads
  const input: QuestionInput = {
    type: (api.input?.type ?? 'SINGLE_SELECT') as QuestionInput['type'],
    title: api.input?.title ?? null,
    placeholder: api.input?.placeholder ?? null,
    validations:
      api.input?.validations != null
        ? {
            minValue: api.input.validations?.minValue,
            maxValue: api.input.validations?.maxValue,
            regex: api.input.validations?.regex ?? undefined
          }
        : null,
    insight: api.input?.insight ?? null,
    inputDataString: api.input?.inputDataString ?? null,
    inputDataNumber: api.input?.inputDataNumber ?? null,
    selectedOptionIds: api.input?.selectedOptionIds ?? null
  }
  const options: QuestionOption[] = (api.options ?? []).map((opt) => ({
    id: opt.id,
    displayText: opt.displayText,
    subText: opt.subText ?? undefined,
    order: opt.order,
    expertRecommended: opt.isRecommended ?? undefined,
    conditionalExpression: opt.conditionalExpression ?? undefined,
    portfolioShare: opt.portfolioShare ?? undefined,
    fundList: opt.fundList ?? undefined,
    deprecated: opt.deprecated ?? undefined,
    metadata: opt.metadata ?? undefined,
    reviewText: opt.reviewText ?? undefined
  }))
  const expertRecommendation =
    api.expertRecommendation != null
      ? {
          displayText: api.expertRecommendation.displayText,
          subText: api.expertRecommendation.subText ?? '',
          idealAllocationPercentage: api.expertRecommendation.idealAllocationPercentage ?? null
        }
      : undefined

  return {
    id: api.id,
    displayText: api.displayText,
    subText: api.subText ?? undefined,
    helperText: api.helperText ?? undefined,
    learnMoreUrl: api.learnMoreUrl ?? undefined,
    portfolioShare: api.portfolioShare ?? undefined,
    input,
    options,
    order: api.order,
    conditionalExpression: api.conditionalExpression ?? undefined,
    expertRecommendation,
    questionType: api.questionType ?? undefined,
    title: api.title ?? null,
    titleSubText: api.titleSubText ?? null,
    disclaimerText: api.disclaimerText ?? null,
    segment: api.segment ?? null,
    answered: api.answered ?? undefined,
    fundList: api.fundList ?? undefined
  }
}
